// إدارة العجلات — قائمة كاملة + إنشاء/تعديل/حذف + بحث + تصفية بالحالة.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import { adminApi, formatApiError } from "../lib/api";
import {
  getEventStatus,
  sortEvents,
  resolveImageUrl,
  STATUS_LABELS,
} from "../lib/eventsUtils";
import AdminHeader from "../components/AdminHeader";
import EventFormModal from "../components/EventFormModal";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  Star,
  ImageOff,
  Filter,
  RefreshCw,
  Calendar,
} from "lucide-react";

const STATUS_TABS = [
  { id: "all", label: "الكل" },
  { id: "live", label: "المتوفرة" },
  { id: "upcoming", label: "القادمة" },
  { id: "ended", label: "المنتهية" },
];

const STATUS_PILL = {
  live: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  upcoming: "bg-sky-500/15 border-sky-500/40 text-sky-200",
  ended: "bg-white/[0.04] border-white/10 text-white/50",
};

const formatShort = (iso) => (iso ? iso.slice(0, 10) : "—");

const EventRow = ({ event, onEdit, onDelete }) => {
  const status = getEventStatus(event);
  const [confirm, setConfirm] = useState(false);
  const imageUrl = resolveImageUrl(event.image);

  return (
    <div
      data-testid={`admin-event-row-${event.id}`}
      className="rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:items-center"
    >
      <div className="w-full sm:w-40 aspect-video rounded-xl overflow-hidden bg-[#060B1E] border border-white/10 flex-shrink-0">
        {imageUrl ? (
          <img src={imageUrl} alt={event.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageOff className="w-6 h-6 text-white/30" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <h3 className="text-white font-black text-base sm:text-lg leading-tight truncate">
            {event.name}
          </h3>
          {event.featured && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[#C9A96A]/15 border border-[#C9A96A]/30 text-[#E5CB94]">
              <Star className="w-2.5 h-2.5" />
              مميزة
            </span>
          )}
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_PILL[status]}`}>
            {STATUS_LABELS[status]}
          </span>
        </div>
        <div className="text-[#B89968] text-[11px] font-medium mt-0.5">{event.type}</div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-white/50 text-[11px]">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            بداية: <span dir="ltr" className="tabular-nums">{formatShort(event.startsAt)}</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            نهاية: <span dir="ltr" className="tabular-nums">{formatShort(event.endsAt)}</span>
          </span>
          {event.wheelId && (
            <span className="inline-flex items-center gap-1 text-white/60">
              الحاسبة: <code className="text-[#E5CB94]">{event.wheelId}</code>
            </span>
          )}
        </div>
      </div>

      <div className="flex sm:flex-col gap-2 items-stretch">
        <button
          type="button"
          data-testid={`admin-event-edit-${event.id}`}
          onClick={() => onEdit(event)}
          className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-[#C9A96A]/15 hover:bg-[#C9A96A]/25 border border-[#C9A96A]/40 text-[#E5CB94] text-[11px] font-bold transition flex-1"
        >
          <Pencil className="w-3.5 h-3.5" />
          تعديل
        </button>
        {confirm ? (
          <div className="flex gap-1">
            <button
              type="button"
              data-testid={`admin-event-delete-confirm-${event.id}`}
              onClick={() => { setConfirm(false); onDelete(event); }}
              className="inline-flex items-center gap-1 h-9 px-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[10px] font-bold transition"
            >
              <Trash2 className="w-3 h-3" />
              تأكيد
            </button>
            <button
              type="button"
              onClick={() => setConfirm(false)}
              className="inline-flex items-center h-9 px-2 rounded-lg bg-white/[0.04] border border-white/10 text-white/70 text-[10px] font-bold transition"
            >
              إلغاء
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`admin-event-delete-${event.id}`}
            onClick={() => setConfirm(true)}
            className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-200 text-[11px] font-bold transition"
          >
            <Trash2 className="w-3.5 h-3.5" />
            حذف
          </button>
        )}
      </div>
    </div>
  );
};

const AdminEventsPage = () => {
  const { isAuthenticated, loading: authLoading } = useAdminAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/events");
      setEvents(data?.items || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchEvents();
  }, [isAuthenticated, fetchEvents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = sortEvents(events);
    if (statusFilter !== "all") list = list.filter((e) => getEventStatus(e) === statusFilter);
    if (q) {
      list = list.filter(
        (e) =>
          (e.name || "").toLowerCase().includes(q) ||
          (e.type || "").toLowerCase().includes(q) ||
          (e.id || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [events, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { all: events.length, live: 0, upcoming: 0, ended: 0 };
    events.forEach((e) => (c[getEventStatus(e)] += 1));
    return c;
  }, [events]);

  const handleCreate = () => {
    setFormInitial(null);
    setFormOpen(true);
  };
  const handleEdit = (event) => {
    setFormInitial(event);
    setFormOpen(true);
  };
  const handleDelete = async (event) => {
    setError("");
    try {
      await adminApi.delete(`/admin/events/${event.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };
  const handleSaved = () => {
    fetchEvents();
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center text-white/50 text-sm">
        جارٍ التحقّق من الجلسة...
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[#0A1128] text-white" dir="rtl">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04] z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative z-10">
        <AdminHeader title="إدارة العجلات" />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
          {/* Toolbar */}
          <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-3 sm:p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-[#C9A96A]" />
              {STATUS_TABS.map((s) => {
                const active = statusFilter === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`admin-events-filter-${s.id}`}
                    onClick={() => setStatusFilter(s.id)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                      active
                        ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]"
                        : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
                    }`}
                  >
                    {s.label}
                    <span className={`ms-1.5 px-1.5 py-0.5 rounded-md text-[9px] ${active ? "bg-[#0A1128]/50 text-[#E5CB94]" : "bg-white/[0.04] text-slate-400"}`}>
                      {counts[s.id]}
                    </span>
                  </button>
                );
              })}
              <div className="ms-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="admin-events-refresh"
                  onClick={fetchEvents}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 text-[11px] font-bold transition"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  تحديث
                </button>
                <button
                  type="button"
                  data-testid="admin-events-create"
                  onClick={handleCreate}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] text-[11px] font-black transition hover:brightness-110"
                >
                  <Plus className="w-4 h-4" />
                  عجلة جديدة
                </button>
              </div>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-events-search"
                placeholder="بحث بالاسم أو النوع أو المعرّف..."
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white ps-9 pe-4 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
              />
            </div>
          </div>

          {error && (
            <div data-testid="admin-events-error" className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3">
              {error}
            </div>
          )}

          {loading && events.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-slate-400 text-center py-16 text-sm inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin" />
              جارٍ التحميل...
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-center py-16 text-sm text-white/60">
              لا توجد عجلات مطابقة.
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {filtered.map((e) => (
                <EventRow key={e.id} event={e} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </main>
      </div>

      <EventFormModal
        open={formOpen}
        initial={formInitial}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default AdminEventsPage;
